import React from 'react';
import { Logger } from '@saviynt/common';
import {
  Chip,
  InlineMessage,
  Tooltip,
  Typography,
} from '@saviynt/design-system';

import { type CertRecommendations } from '../../../../services/types';
import { useInvertedLocalize } from '../../../../utils';

import RECOMMENDATION_IDS, { CERTIFICATION_THRESHOLDS } from './constants';
import invertedMessages from './messages';

import './Recommendations.css';

interface RecommendationWidgetProps {
  title: string;
  count: number;
  badge?: React.ReactNode;
}

function RecommendationWidget({
  title,
  count,
  badge,
}: RecommendationWidgetProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <div className='Ext-Recommendations-widget'>
      <div className='Ext-Recommendations-widget-header'>
        <div className='Ext-Recommendations-widget-header'>
          <Typography kind='h2'>{title}</Typography>
        </div>
        {badge}
      </div>
      <Typography kind='display1'>{count}</Typography>
      <InlineMessage
        text={l('Since last 12 months')}
        colorTheme='disabled'
        className=''
      />
    </div>
  );
}

RecommendationWidget.defaultProps = {
  badge: null,
};

interface RecommendationsProps {
  data: CertRecommendations[];
}

export default function Recommendations({ data }: RecommendationsProps) {
  const l = useInvertedLocalize(invertedMessages);
  let certifiedRecommendations = {} as CertRecommendations;
  let unCertifiedRecommendations = {} as CertRecommendations;

  data.forEach((recommendation) => {
    if (recommendation.id === RECOMMENDATION_IDS.USER_CERTIFICATIONS) {
      certifiedRecommendations = recommendation;
    } else if (
      recommendation.id === RECOMMENDATION_IDS.UNCERTIFIED_ACTIVE_USERS
    ) {
      unCertifiedRecommendations = recommendation;
    } else {
      Logger.warn(`Unexpected recommendation: ${recommendation.id}`);
    }
  });

  return (
    <div className='Ext-Recommendations'>
      <RecommendationWidget
        title={l('Average User Manager Certifications')}
        count={certifiedRecommendations.count}
        badge={
          <Tooltip
            trigger={
              <>
                {certifiedRecommendations.count <=
                  CERTIFICATION_THRESHOLDS.LOW && (
                  <Chip kind='outlined' label={l('Low')} color='critical' />
                )}
                {certifiedRecommendations.count >=
                  CERTIFICATION_THRESHOLDS.MEDIUM.MIN &&
                  certifiedRecommendations.count <=
                    CERTIFICATION_THRESHOLDS.MEDIUM.MAX && (
                    <Chip kind='outlined' label={l('Medium')} color='warning' />
                  )}
                {certifiedRecommendations.count >=
                  CERTIFICATION_THRESHOLDS.HIGH && (
                  <Chip kind='outlined' label={l('High')} color='success' />
                )}
              </>
            }
            text={l(
              'External identities should be certified with an average of once in a quarter'
            )}
            placement='top-start'
          />
        }
      />
      <RecommendationWidget
        title={l('Uncertified Active Users')}
        count={unCertifiedRecommendations.count}
      />
    </div>
  );
}
